/**
 * Cloudflare's Email Address Obfuscation (Scrape Shield) rewrites any email
 * address or mailto: link it finds in the HTTP response body into a
 * /cdn-cgi/l/email-protection link with "[email protected]" placeholder text,
 * decoded back only by a client-side script. Our marketing routes (contact,
 * privacy, terms, acceptable-use) are prerendered to static HTML for
 * crawlers/bots that never run that script, so those visitors see garbage
 * instead of a real address (DF-252).
 *
 * Cloudflare's documented escape hatch is wrapping the address in an
 * <!--email_off--> HTML comment pair, which its edge rewriter skips over —
 * including the href of an <a> tag if the whole tag sits inside the pair.
 * JSX can't emit a literal HTML comment node, so this renders the markup via
 * dangerouslySetInnerHTML; safe here because every caller passes a static,
 * hardcoded address, never user input.
 */
function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/** Wraps a static mailto: link so Cloudflare's edge leaves it alone. */
export function EmailLink({
  email,
  subject,
  className,
  children,
}: {
  email: string;
  subject?: string;
  className?: string;
  children?: string;
}) {
  const href = `mailto:${email}${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`;
  const label = children ?? email;
  const classAttr = className ? ` class="${escapeHtml(className)}"` : '';
  const html = `<!--email_off--><a href="${escapeHtml(href)}"${classAttr}>${escapeHtml(label)}</a><!--/email_off-->`;
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Wraps any email address(es) found inside a static string of plain text. */
export function EmailText({ text, className }: { text: string; className?: string }) {
  const html = escapeHtml(text).replace(EMAIL_RE, (m) => `<!--email_off-->${m}<!--/email_off-->`);
  return <span className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}
